import './styles/navbar.scss'
import Logo from "./Logo";
import {useState, useEffect} from 'react';
import NavButton from "./NavButton";
import Football1Icon from "./icons/Football1Icon";
import LiveIcon from "./icons/LiveIcon";
import CasinoIcon from "./icons/CasinoIcon";
import DiceIcon from "./icons/DiceIcon";
import GameIcon from "./icons/GameIcon";
import ShieldIcon from "./icons/ShieldIcon";
import HamburgerIcon from "./icons/HamburgerIcon";
import CloseIcon from "./icons/CloseIcon";
import UserIcon from "./icons/UserIcon";
import {useLanguage} from "../contexts/LanguageProvider";
import {useLocation} from "react-router-dom";

const Navbar = () => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isLargeScreen, setIsLargeScreen] = useState(false);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

    const language = useLanguage();
    const location = useLocation();

    useEffect(() => {
        const throttle = (func, limit) => {
            let inThrottle;
            return function(...args) {
                if (!inThrottle) {
                    func.apply(this, args);
                    inThrottle = true;
                    setTimeout(() => inThrottle = false, limit);
                }
            };
        };

        const checkScreenSize = throttle(() => {
            const width = window.innerWidth;
            setIsMobile(width < 985);
            setIsLargeScreen(width >= 1185);
        }, 250); // Execute at most every 250ms

        // Initial check
        checkScreenSize();

        // Add event listener with cleanup
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const toggleUserDropdown = () => {
        setIsUserDropdownOpen(!isUserDropdownOpen);
    };

    const handleLanguageSelect = (lang) => {
        language.changeLanguage(lang);
        setIsDropdownOpen(false);
    };

    const handleLoginClick = () => {
        setIsUserDropdownOpen(false);
        toggleMobileMenu();
        // Add login functionality here
    };

    const handleSignUpClick = () => {
        setIsUserDropdownOpen(false);
        toggleMobileMenu();
        // Add signup functionality here
    };

    return (
        <nav className='navbar-root'>
            <div className='navbar-container container max-w-screen-xl mx-auto px-2 py-4'>
                <Logo />

                {isMobile ? (
                    <div className='mobile-header-actions'>
                        <div className="language-selector">
                            <button
                                className="flex uppercase items-center gap-2 px-3 py-1 rounded text-white inter-semibold"
                                onClick={toggleDropdown}
                            >
                                {language.currentLanguage}
                                <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 8.37501L0 2.37501L1.4 0.975006L6 5.57501L10.6 0.975006L12 2.37501L6 8.37501Z" fill="white"/>
                                </svg>
                            </button>
                            {isDropdownOpen && (
                                <div className="absolute mt-1 w-20 bg-black shadow-lg rounded-md overflow-hidden">
                                    {['EN', 'AR', 'FR'].map((lang) => (
                                        <button
                                            key={lang}
                                            className="w-full px-3 py-2 text-left hover:bg-gray-100"
                                            onClick={() => handleLanguageSelect(lang)}
                                        >
                                            <span className='uppercase'>{lang}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button className='mobile-menu-toggle' onClick={toggleMobileMenu}>
                            {isMobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className='nav-links'>
                            <NavButton isSelected={location.pathname === '/'} text={language.t('sport')} icon={<Football1Icon />} href='/' />
                            <NavButton isSelected={location.pathname === '/live'} text={language.t('live')} icon={<LiveIcon />} href='/live' />
                            <NavButton isSelected={location.pathname === '/casino'} text={language.t('casino')} icon={<DiceIcon />} href='/casino' />
                            <NavButton isSelected={location.pathname === '/live-casino'} text={language.t('liveCasino')} icon={<CasinoIcon />} href='/live-casino' />
                            <NavButton isSelected={location.pathname === '/virtual-games'} text={language.t('virtualGames')} icon={<GameIcon />} href='/virtual-games' />
                            <div className='seperator' />
                            <NavButton isSelected={location.pathname === '/promotion'} className='highlight-button' text={language.t('promotion')} icon={<ShieldIcon />} href='/promotion' />
                        </div>

                        <div className='nav-actions'>
                            <div className="language-selector">
                                <button
                                    className="flex uppercase items-center gap-2 px-3 py-1 rounded text-white inter-semibold"
                                    onClick={toggleDropdown}
                                >
                                    {language.currentLanguage}
                                    <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 8.37501L0 2.37501L1.4 0.975006L6 5.57501L10.6 0.975006L12 2.37501L6 8.37501Z" fill="white"/>
                                    </svg>
                                </button>
                                {isDropdownOpen && (
                                    <div className="absolute top-full mt-1 w-20 bg-black shadow-lg rounded-md overflow-hidden">
                                        {['EN', 'AR', 'FR'].map((lang) => (
                                            <button
                                                key={lang}
                                                className="w-full px-3 py-2 text-left hover:bg-yellow-600"
                                                onClick={() => handleLanguageSelect(lang)}
                                            >
                                                <span className='uppercase'>{lang}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {isLargeScreen ? (
                                <>
                                    <button className='button'>{language.t('login')}</button>
                                    <button className='button solid'>{language.t('signUp')}</button>
                                </>
                            ) : (
                                <div className="user-dropdown-container">
                                    <button 
                                        className="user-icon-button"
                                        onClick={toggleUserDropdown}
                                    >
                                        <UserIcon />
                                    </button>
                                    {isUserDropdownOpen && (
                                        <div className="user-dropdown">
                                            <button className='dropdown-button' onClick={handleLoginClick}>{language.t('login')}</button>
                                            <button className='dropdown-button solid' onClick={handleSignUpClick}>{language.t('signUp')}</button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className='mobile-menu'>
                    <div className='mobile-nav-links'>
                        <NavButton isSelected={location.pathname === '/'} text={language.t('sport')} icon={<Football1Icon />} href='/' onClick={toggleMobileMenu} />
                        <NavButton isSelected={location.pathname === '/live'} text={language.t('live')} icon={<LiveIcon />} href='/live' onClick={toggleMobileMenu} />
                        <NavButton isSelected={location.pathname === '/casino'} text={language.t('casino')} icon={<DiceIcon />} href='/casino' onClick={toggleMobileMenu} />
                        <NavButton isSelected={location.pathname === '/live-casino'} text={language.t('liveCasino')} icon={<CasinoIcon />} href='/live-casino' onClick={toggleMobileMenu} />
                        <NavButton isSelected={location.pathname === '/virtual-games'} text={language.t('virtualGames')} icon={<GameIcon />} href='/virtual-games' onClick={toggleMobileMenu} />
                        <NavButton isSelected={location.pathname === '/promotion'} className='highlight-button' text={language.t('promotion')} icon={<ShieldIcon />} href='/promotion' onClick={toggleMobileMenu} />
                    </div>

                    <div className='mobile-nav-actions'>
                        <div className='mobile-buttons'>
                            <button className='button' onClick={toggleMobileMenu}>{language.t('login')}</button>
                            <button className='button solid' onClick={toggleMobileMenu}>{language.t('signUp')}</button>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    )
}

export default Navbar;
